import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, first, map, mergeMap, switchMap } from 'rxjs';
import { SelectionAction, ExportSelection } from 'gung-list';
import { Product } from '../../models/product';
import { ProductExportService } from '../product-export/product-export.service';
import { AuthService } from '../auth/auth.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ProductsBookmarkManagementModalComponent } from '../../components/products-bookmark/products-bookmark-management-modal/products-bookmark-management-modal.component';
import { MongoAssortmentManagementModalComponent } from '../../components/mongo-assortment-management-modal/mongo-assortment-management-modal.component';
import { Router } from '@angular/router';
import { DocumentTemplatesService } from '../document-templates/document-templates.service';
import { Template } from 'gung-common';
import { SelectProductImageExportSizeModalComponent } from '../../components/select-product-image-export-size-modal/select-product-image-export-size-modal.component';
import { ExportDragProductService } from 'gung-dynamic-pdf';
import { NavbarConfigService } from '../navbar-config/navbar-config.service';
import { BackendFeatureService } from '../backend-feature.service';
import { TranslateService } from '@ngx-translate/core';
import { GungFlowService } from '../gung-flow/gung-flow.service';
import { PdfDocumentBuilderService } from '../../modules/pdf-document-builder/services/pdf-document-builder/pdf-document-builder.service';
import { PdfDocumentTemplate } from '../../modules/pdf-document-builder/models/pdf-document-builder.model';
import { GungNotificationService } from 'gung-common';
import { DownloadFileService } from '../file-downloader/download-file.service';
import { PdfDownloadIndicatorService } from '../pdf-download-indicator.service';
import { HttpResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class ProductExportActionConfigService {
  protected productExportService = inject(ProductExportService);
  protected authService = inject(AuthService);
  protected modalService = inject(NgbModal);
  protected router = inject(Router);
  protected documentTemplatesService = inject(DocumentTemplatesService);
  protected navbarConfigService = inject(NavbarConfigService);
  protected exportDragProductService = inject(ExportDragProductService);
  protected pdfDocumentBuilderService = inject(PdfDocumentBuilderService);
  protected gungFlowService = inject(GungFlowService);
  protected translateService = inject(TranslateService);
  protected gungNotificationService = inject(GungNotificationService);
  protected downloadFileService = inject(DownloadFileService);
  protected pdfDownloadIndicatorService = inject(PdfDownloadIndicatorService);

  public modalRef: Map<string, NgbModalRef> = new Map<string, NgbModalRef>();
  private enableExportPdf = this.navbarConfigService.enableExportPdf;
  protected backendFeatureService = inject(BackendFeatureService);

  userRoles(): Observable<string[]> {
    return this.authService.getRoles().pipe(filter(roles => !!roles));
  }
  getDynamicSelectionActions(): Observable<SelectionAction<Product>[]> {
    return forkJoin([
      this.authService.getRoles().pipe(first()),
      this.documentTemplatesService.getTemplates().pipe(first())
    ]).pipe(
      first(),
      mergeMap(([roles, templates]) => {
        const dynamicActions = [];
        templates.forEach(template => {
          if (
            template.type !== 'CUSTOMER' &&
            template.showXlsxAutomatically &&
            this.matchesAnyUserRole(template, roles)
          ) {
            dynamicActions.push(this.getExportActionFromTemplate(template));
          }
        });
        return of(dynamicActions);
      })
    );
  }

  getSelectionActions(): Observable<SelectionAction<Product>[]> {
    return this.authService.getRoles().pipe(
      first(),
      switchMap(roles => {
        let userActions: SelectionAction<Product>[] = [
          {
            label: 'PRODUCT_EXPORT_XLSX',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportExcel(itemList).pipe(first()).subscribe();
              return of();
            }
          },
          {
            label: 'PRODUCT_EXPORT_IMG',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              this.productExportService.exportImg(itemList).pipe(first()).subscribe();
              return of();
            }
          }
        ];
        if (roles.includes('SALES') || roles.includes('ADMIN')) {
          userActions = [
            {
              label: 'PRODUCT_EXPORT_XLSX',
              performAction: (selection: ExportSelection<Product>) => {
                const itemList = Object.values(selection.selectedItems);
                this.productExportService.exportExcel(itemList).pipe(first()).subscribe();
                return of();
              }
            },
            {
              label: 'PRODUCT_EXPORT_CSV',
              performAction: (selection: ExportSelection<Product>) => {
                const itemList = Object.values(selection.selectedItems);
                this.productExportService.exportCsv(itemList).pipe(first()).subscribe();
                return of();
              }
            },
            {
              label: 'PRODUCT_EXPORT_LINESHEET',
              performAction: (selection: ExportSelection<Product>) => {
                const itemList = Object.values(selection.selectedItems);
                this.productExportService.exportLinesheet(itemList).pipe(first()).subscribe();
                return of();
              }
            },
            {
              label: 'PRODUCT_EXPORT_PRICAT',
              performAction: (selection: ExportSelection<Product>) => {
                const itemList = Object.values(selection.selectedItems);
                this.productExportService.exportPricat(itemList).pipe(first()).subscribe();
                return of();
              }
            },
            {
              label: 'PRODUCT_EXPORT_IMG',
              performAction: (selection: ExportSelection<Product>) => {
                const itemList = Object.values(selection.selectedItems);
                const id = SelectProductImageExportSizeModalComponent.name;
                const ref = this.modalService.open(SelectProductImageExportSizeModalComponent, {
                  size: 'md',
                  windowClass: 'modal-md-custom'
                });
                ref.componentInstance.items = itemList;
                ref.result.then(
                  result => {
                    if (result.success) {
                      this.productExportService
                        .exportImg(itemList, {
                          imageTransformations: result.imageTransformations,
                          format: result.format
                        })
                        .pipe(first())
                        .subscribe();
                    }
                  },
                  reason => {}
                );
                this.modalRef.set(id, ref);
                return of();
              }
            },
            {
              label: 'PRODUCT_EXPORT_PRODUCT_INFO',
              performAction: (selection: ExportSelection<Product>) => {
                const itemList = Object.values(selection.selectedItems);
                this.productExportService.exportProductInfo(itemList).pipe(first()).subscribe();
                return of();
              }
            },
            {
              label: 'PRODUCT_EXPORT_PRODUCT_STOCK',
              performAction: (selection: ExportSelection<Product>) => {
                const itemList = Object.values(selection.selectedItems);
                this.productExportService.exportProductStock(itemList).pipe(first()).subscribe();
                return of();
              }
            }
          ];
        }
        return this.backendFeatureService.isActivated('gung-feature-edi.pricats').pipe(
          map(pricatsEnabled => {
            if (pricatsEnabled && (roles.includes('SALES') || roles.includes('ADMIN'))) {
              userActions.push({
                label: 'PRODUCT_EXPORT_EDI_PRICAT',
                performAction: (selection: ExportSelection<Product>) => {
                  const itemList = Object.values(selection.selectedItems);
                  this.productExportService.exportEdiPricat(itemList).pipe(first()).subscribe();
                  return of();
                }
              });
            }
            return userActions;
          })
        );
      })
    );
  }

  matchesAnyUserRole(template: Template, roles: string[]): boolean {
    if (!roles || !template.availableToRoles) {
      return false;
    }
    let matchesRole = false;
    roles.forEach(role => {
      if (template.availableToRoles.includes(role)) {
        matchesRole = true;
      }
    });
    return matchesRole;
  }
  getExportActionFromTemplate(template: Template): SelectionAction<Product> {
    return {
      label: template.name,
      performAction: (selection: ExportSelection<Product>) => {
        const itemList = Object.values(selection.selectedItems);
        if (template.type === 'PRODUCT') {
          if (template.id.endsWith('_CSV')) {
            this.productExportService.exportCsv(itemList, template.id).pipe(first()).subscribe();
          } else if (template.id.endsWith('_TSV')) {
            this.productExportService.exportTsv(itemList, template.id).pipe(first()).subscribe();
          } else {
            this.productExportService.exportExcel(itemList, template.id).pipe(first()).subscribe();
          }
        } else if (template.type === 'B24_PRICAT') {
          this.productExportService.exportPricat(itemList).pipe(first()).subscribe();
        } else {
          console.log('NO MATCHING export found. Please check you configuration');
        }

        return of();
      },
      groupName: template.groupName || ''
    };
  }
  getPdfDocumentBuilderExportActions(documentTypeId: string): Observable<SelectionAction<Product>[]> {
    return this.gungFlowService.getSelectedFlow().pipe(
      first(),
      switchMap(flow =>
        this.pdfDocumentBuilderService.getVisibleExports({ documentTypeId, flowId: flow?.id }).pipe(
          map(templates => {
            const locale = this.translateService.currentLang || 'en';
            return templates.map(template => this.getPdfDocumentBuilderExportAction(template, locale));
          }),
          catchError(() => of([] as SelectionAction<Product>[]))
        )
      )
    );
  }

  protected getPdfDocumentBuilderExportAction(
    template: PdfDocumentTemplate,
    locale: string
  ): SelectionAction<Product> {
    const label =
      template.displayName?.[locale] ||
      template.displayName?.['en'] ||
      template.name;
    return {
      label,
      performAction: (selection: ExportSelection<Product>) => {
        const entityIds = Object.keys(selection.selectedItems);

        this.pdfDownloadIndicatorService.setDownloading({ downloading: true, data: null });

        this.pdfDocumentBuilderService
          .renderPdf({ templateGroupId: template.templateGroupId, entityIds })
          .pipe(first())
          .subscribe({
            next: response => {
              this.pdfDownloadIndicatorService.setDownloading({ downloading: false, data: null });
              if (!response.body) {
                return;
              }
              // The DownloadFileService extracts the filename from Content-Disposition
              // and triggers a normal browser download — no popup-blocker concerns.
              const file = this.downloadFileService.create(response as unknown as HttpResponse<ArrayBuffer>);
              this.downloadFileService.download(file);
            },
            error: () => {
              this.pdfDownloadIndicatorService.setDownloading({ downloading: false, data: null });
              this.gungNotificationService.notify(
                this.translateService.instant('ERROR'),
                this.translateService.instant('PDF_EXPORT_FAILED'),
                'error'
              );
            }
          });
        return of();
      }
    };
  }

  getSelectionMarkingActions(): Observable<SelectionAction<Product>[]> {
    return this.authService.getRoles().pipe(
      first(),
      map(roles => {
        const actions: SelectionAction<Product>[] = [
          {
            label: 'BOOKMARKS',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.values(selection.selectedItems);
              const modal = this.modalService.open(ProductsBookmarkManagementModalComponent, { size: 'xl' });
              modal.componentInstance.products = itemList;
              return of();
            }
          }
        ];
        if (roles.includes('ADMIN')) {
          actions.push({
            label: 'ASSORTMENTS',
            performAction: (selection: ExportSelection<Product>) => {
              const modal = this.modalService.open(MongoAssortmentManagementModalComponent, { size: 'xl' });
              return of();
            }
          });
        }
        if (this.enableExportPdf) {
          actions.push({
            label: 'SEND_TO_PDF_EXPORT',
            performAction: (selection: ExportSelection<Product>) => {
              const itemList = Object.keys(selection.selectedItems);
              this.exportDragProductService.addListOfProducts(itemList);
              this.router.navigate(['export-pdf/new']);
              return of();
            }
          });
        }
        return actions;
      })
    );
  }
}
